import React, { useEffect, useRef, useState } from 'react';

import { track } from 'src/tracking/trackingService';

import type { InjectType, SharedProps, ValidComponent } from 'reconnect-types/moduleFederation';

import { fetchRemoteModule } from './fetchModule';
import { buildPageConfig } from './pageConfig';
import styles from './Reconnect.module.css';

const remoteUrl = '/reconnect/remoteEntry.js';

export const useRemoteModule = <T,>(scope: string, module: string) => {
  const [loadedModule, setModule] = useState<T | null>(null);

  useEffect(() => {
    fetchRemoteModule({
      remoteUrl,
      scope,
      module,
    })
      .then((fetchedModule) => {
        setModule(fetchedModule);
      })
      .catch(() => {});
  }, [module, scope]);

  return loadedModule;
};

export type InitialiseI18n = (language: string) => Promise<unknown>;

const getConnectTrackingService = (): TrackingService => ({
  trackEvent: ({ name, payload }) => track(name, payload),
});

export const Reconnect = <C extends ValidComponent>({
  name,
  props,
}: {
  name: C;
  props?: SharedProps[C];
}): JSX.Element => {
  const remoteModule = useRemoteModule<{
    inject: InjectType;
    initialiseI18n: InitialiseI18n;
    RECONNECT_VERSION?: string;
  }>('reconnect', './remoteEntry');
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!remoteModule || !ref.current) {
      return;
    }
    // Exposed for the host's datadogRum beforeSend fallback (see app.js) to
    // tag reconnect-attributed events with reconnect's own build version.
    (window as typeof window & { __reconnectVersion?: string }).__reconnectVersion =
      remoteModule.RECONNECT_VERSION;
    const config: Config = {
      ...buildPageConfig(),
      trackingService: props?.trackingService ?? getConnectTrackingService(),
    };
    const unmount = remoteModule.inject<C>({
      node: ref.current,
      name,
      config,
      props,
    });
    return () => unmount();
  }, [remoteModule, name, props]);
  return <div className={styles.reconnect} ref={ref} />;
};
